import React from 'react';
import { Box, ButtonV2, DialogV2, DialogV2Props, DrawerV2, DrawerV2Props, Flex, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { useRouterHelper } from '@shared/core';
import { Link } from '@react-router';
import { useDashboardTelemetry } from '../../../Dashboard.telemetry';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { usePromoBannerConfig } from '@apps/card';

interface Props
  extends Readonly<{
    isOpen: boolean;
    onClose: () => void;
  }> {}

const VIDEO_THUMBNAIL_URL = 'https://i.vimeocdn.com/video/1845048592-0cb090b059f2399554603de4846120ef915c1d040213a6f034af86b068ebc6a0-d_640x360?r=pad';
const VIDEO_URL = 'https://player.vimeo.com/video/941810645?h=304e2a0595&badge=0&autopause=0&player_id=0&app_id=58479&background=1';
const DRAWER_OVERRIDES: DrawerV2Props['overrides'] = {
  Content: {
    props: {
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4
    }
  },
  Body: {
    props: {
      paddingTop: [7, 7]
    }
  }
};

const DIALOG_OVERRIDES: DialogV2Props['overrides'] = {
  CloseButton: {
    props: {
      top: 4,
      right: 4
    }
  },
  Body: {
    props: {
      paddingY: [7, 7]
    }
  }
};

const Video = () => (
  <Box
    marginTop={[7, null, 0]}
    aspectRatio={'1'}
    backgroundImage={`url('${VIDEO_THUMBNAIL_URL}')`}
    backgroundSize="cover"
    backgroundPosition="center"
    flexShrink={0}
    borderRadius={'12px'}
    overflow="hidden"
    width={['100%', null, '50%']}
    height={['100%', null, '50%']}
    minWidth={{ md4: 400 }}
    position="relative"
  >
    <Box
      as="iframe"
      src={VIDEO_URL}
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
      position="absolute"
      top={0}
      left={0}
      width={'100%'}
      height={'100%'}
      border="none"
    />
  </Box>
);

const Content = ({ isMobile, onCtaClick }: { isMobile: boolean; onCtaClick: () => void }) => {
  const { bannerPromoConfig } = usePromoBannerConfig();
  const isPromotionBannerEnabled = !!bannerPromoConfig;

  useDashboardTelemetry({ page: 'printDialog' });

  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('adminDashboardV2');

  const trans = t('printEarlyAccessDialog', 'legacyContent');

  const descriptionTrans = trans[isPromotionBannerEnabled ? 'promo' : 'nonPromo'].description;

  return (
    <Flex flexDirection={['column', null, 'row']} columnGap={8} alignItems={'center'}>
      {!isMobile && <Video />}

      <Flex flexDirection="column" textAlign="center">
        <TextV2 typographyVariant={['hed4', null, 'hed6']}>{trans.eyebrow()}</TextV2>
        <TextV2 typographyVariant={['display3', null, 'display5']} fontWeight="semibold">
          {trans.title()}
        </TextV2>
        {isMobile && <Video />}
        <TextV2 typographyVariant="body2" marginTop={[7, null, 5]} maxWidth={440}>
          {descriptionTrans({ percentageDiscount: isPromotionBannerEnabled ? `${bannerPromoConfig.discountPercentage * 100}%` : undefined })}
        </TextV2>

        <ButtonV2 onClick={onCtaClick} as={Link} to={routeHelpers.buildPath('/cards')} intent="neutral" shape="rounded" marginTop={7}>
          {trans.cta()}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};

export const LegacyContent: React.FC<Props> = props => {
  const { isOpen, onClose } = props;
  const isMobile = useIsMobileScreen();

  const { printEarlyAccessDialogCTAClicked, printEarlyAccessDialogDismissClicked } = useDashboardTelemetry();

  const handleCTAClick = () => {
    printEarlyAccessDialogCTAClicked();
  };

  const handleDialogDismiss = () => {
    printEarlyAccessDialogDismissClicked();
    onClose();
  };

  if (isMobile) {
    return (
      <DrawerV2 disableAutoFocus size="auto" overrides={DRAWER_OVERRIDES} isOpen={isOpen} onClose={handleDialogDismiss} anchor="bottom" useBackdrop>
        <DrawerV2.Body>
          <DrawerV2.CloseButton />
          <Content isMobile onCtaClick={handleCTAClick} />
        </DrawerV2.Body>
      </DrawerV2>
    );
  }

  return (
    <DialogV2 disableAutoFocus overrides={DIALOG_OVERRIDES} isOpen={isOpen} onClose={handleDialogDismiss} size="3xl">
      <DialogV2.CloseButton />
      <DialogV2.Body>
        <Content isMobile={false} onCtaClick={handleCTAClick} />
      </DialogV2.Body>
    </DialogV2>
  );
};
